import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { apiGet, apiPost } from '../api';
import { useT } from '../i18n';
import DashboardLayout from '../components/DashboardLayout';

export default function ProfilePage() {
  const navigate = useNavigate();
  const accessToken = useStore((s) => s.accessToken);
  const t = useT();

  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [verified, setVerified] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [verifyStep, setVerifyStep] = useState('idle');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const showMessage = useCallback((setter, msg) => {
    setter(msg);
    if (msg?.type === 'success') {
      setTimeout(() => setter(null), 5000);
    }
  }, []);

  const startCooldown = useCallback(() => {
    setResendCooldown(300);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  useEffect(() => {
    if (!accessToken) { navigate('/'); return; }
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const loadProfile = async () => {
    try {
      const res = await apiGet('/api/v1/users/me');
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          setUserId(data.data.id || '');
          setName(data.data.name || '');
          setEmail(data.data.email || '');
          setVerified(!!data.data.verified);
        }
      }
    } catch { /* ignore */ }
    setProfileLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const body = { id: userId || '', name, email, password: password || '' };
      const res = await apiPost('/api/v1/users', body);
      const data = await res.json();
      if (data.success) {
        showMessage(setMessage, { type: 'success', text: password ? t('profilePage.passwordUpdated') : t('profilePage.profileUpdated') });
        setPassword('');
        if (data.data?.id) setUserId(data.data.id);
        if (data.data?.verified !== undefined) setVerified(data.data.verified);
        if (data.data?.newToken) {
          useStore.getState().setToken(data.data.newToken);
        }
      } else {
        setMessage({ type: 'error', text: data.message || 'Update failed' });
      }
    } catch {
      setMessage({ type: 'error', text: t('error.connection') });
    }
    setLoading(false);
  };

  const handleSendCode = async () => {
    setVerifyLoading(true);
    setVerifyMessage(null);
    try {
      const res = await apiPost('/api/v1/verification/send', {});
      const data = await res.json();
      if (data.success) {
        setVerifyStep('sent');
        showMessage(setVerifyMessage, { type: 'success', text: t('profilePage.codeSent') });
        startCooldown();
      } else {
        setVerifyMessage({ type: 'error', text: data.message || 'Failed to send code' });
      }
    } catch {
      setVerifyMessage({ type: 'error', text: t('error.connection') });
    }
    setVerifyLoading(false);
  };

  const handleVerifyCode = async () => {
    if (!verifyCode.trim()) return;
    setVerifyLoading(true);
    setVerifyMessage(null);
    try {
      const res = await apiPost('/api/v1/verification/verify', { code: verifyCode });
      const data = await res.json();
      if (data.success) {
        setVerified(true);
        setVerifyStep('idle');
        setVerifyCode('');
        showMessage(setVerifyMessage, { type: 'success', text: t('profilePage.emailVerified') });
      } else {
        setVerifyMessage({ type: 'error', text: data.message || 'Invalid code' });
      }
    } catch {
      setVerifyMessage({ type: 'error', text: t('error.connection') });
    }
    setVerifyLoading(false);
  };

  const handleResendCode = async () => {
    setVerifyLoading(true);
    setVerifyMessage(null);
    try {
      const res = await apiPost('/api/v1/verification/resend', {});
      const data = await res.json();
      if (data.success) {
        showMessage(setVerifyMessage, { type: 'success', text: t('profilePage.codeResent') });
        startCooldown();
      } else {
        setVerifyMessage({ type: 'error', text: data.message || 'Failed to resend' });
      }
    } catch {
      setVerifyMessage({ type: 'error', text: t('error.connection') });
    }
    setVerifyLoading(false);
  };

  if (profileLoading) {
    return (
      <DashboardLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Loading...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="profile-content">
        <h1 className="profile-title">{t('profilePage.title')}</h1>

        {message && (
          <div className={`profile-message ${message.type}`}>{message.text}</div>
        )}

        <div className="profile-card">
          <h3 className="profile-card-title">{t('profilePage.personal')}</h3>
          <form onSubmit={handleSave} className="profile-form">
            <div className="profile-field">
              <label>{t('auth.name')}</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('auth.namePlaceholder')} required />
            </div>
            <div className="profile-field">
              <div className="profile-label-row">
                <label>{t('auth.email')}</label>
                <span className={`verify-badge ${verified ? 'verified' : 'unverified'}`}>
                  {verified ? (
                    <><svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6.5l2 2 4-4" /></svg> {t('profilePage.verified')}</>
                  ) : (
                    <><svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="6" cy="6" r="4" /><path d="M6 4v2.5M6 8h.01" /></svg> {t('profilePage.notVerified')}</>
                  )}
                </span>
              </div>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('auth.emailPlaceholder')} required />
              {!verified && verifyStep === 'idle' && (
                <div className="verify-prompt">
                  <p className="verify-prompt-text">{t('profilePage.verifyHint')}</p>
                  <button type="button" className="verify-send-btn" onClick={handleSendCode} disabled={verifyLoading}>
                    {verifyLoading ? t('profilePage.sending') : t('profilePage.sendCode')}
                  </button>
                </div>
              )}
              {verifyStep === 'sent' && (
                <div className="verify-code-box">
                  <p className="verify-code-text">{t('profilePage.enterCode')} <strong>{email}</strong></p>
                  <div className="verify-code-row">
                    <input className="verify-code-input" value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)} placeholder="Enter code" maxLength={6} />
                    <button type="button" className="verify-confirm-btn" onClick={handleVerifyCode} disabled={verifyLoading || !verifyCode.trim()}>
                      {verifyLoading ? '...' : t('profilePage.verify')}
                    </button>
                  </div>
                  <button type="button" className="verify-resend" onClick={handleResendCode} disabled={verifyLoading || resendCooldown > 0}>
                    {resendCooldown > 0
                      ? `${t('profilePage.resendIn')} ${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, '0')}`
                      : t('profilePage.resend')}
                  </button>
                </div>
              )}
              {verifyMessage && (
                <div className={`verify-msg ${verifyMessage.type}`}>{verifyMessage.text}</div>
              )}
            </div>

            <div className="profile-divider" />

            <h3 className="profile-card-subtitle">{t('profilePage.changePassword')}</h3>
            <p className="profile-hint">{t('profilePage.passwordHint')}</p>
            <div className="profile-field">
              <label>{t('profilePage.newPassword')}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('profilePage.newPasswordPlaceholder')} minLength={6} />
            </div>

            <button className="profile-save-btn" type="submit" disabled={loading}>
              {loading ? t('profilePage.saving') : t('profilePage.save')}
            </button>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
